import React, { useState, useEffect, useRef, useCallback } from "react"
import styled, { keyframes } from "styled-components"
import { FaVolumeUp } from "@react-icons/all-files/fa/FaVolumeUp"
import { FaPause } from "@react-icons/all-files/fa/FaPause"
import { FaPlay } from "@react-icons/all-files/fa/FaPlay"
import { FaTimes } from "@react-icons/all-files/fa/FaTimes"

/**
 * TextToSpeech Component
 * 
 * Uses browser Web Speech API with native pause/resume support.
 * 
 * @param {string} text - Content to be read aloud (required)
 * @param {number} rate - Speech rate (0.1 to 10, default: 1.0)
 * @param {string} lang - Language code (default: 'en-IN')
 * @param {boolean} disabled - Disable the component (default: false)
 * @param {function} onStart - Callback when speech starts
 * @param {function} onStop - Callback when speech stops
 * @param {function} onEnd - Callback when speech ends naturally
 * @param {function} onPause - Callback when speech pauses
 * @param {function} onResume - Callback when speech resumes
 */
const TextToSpeech = ({
  text = "",
  rate = 1.0,
  lang = "en-IN",
  disabled = false,
  onStart,
  onStop,
  onEnd,
  onPause,
  onResume,
}) => {
  // State for UI
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [isSupported, setIsSupported] = useState(false)

  // Refs
  const utteranceRef = useRef(null)

  // Check browser support
  useEffect(() => {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      setIsSupported(true)
    }
  }, [])

  /**
   * Reset state to idle
   */
  const resetState = useCallback(() => {
    setIsPlaying(false)
    setIsPaused(false)
    utteranceRef.current = null
  }, [])

  /**
   * Start speaking
   */
  const startSpeech = useCallback(() => {
    if (!isSupported || !text || disabled) return

    try {
      // Cancel any existing speech first
      window.speechSynthesis.cancel()

      // Create new utterance
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.rate = Math.max(0.1, Math.min(10, rate))
      utterance.lang = lang
      utterance.volume = 1.0

      // Handle natural end
      utterance.onend = () => {
        resetState()
        onEnd?.()
      }

      // Handle errors
      utterance.onerror = (event) => {
        // Ignore interrupted errors (happens on manual stop)
        if (event.error === "interrupted" || event.error === "canceled") {
          return
        }
        console.error("Speech error:", event.error)
        resetState()
      }

      // Store reference
      utteranceRef.current = utterance

      // Start speaking
      window.speechSynthesis.speak(utterance)

      // Update state
      setIsPlaying(true)
      setIsPaused(false)

      onStart?.()
    } catch (error) {
      console.error("Start speech error:", error)
      resetState()
    }
  }, [isSupported, text, disabled, rate, lang, resetState, onStart, onEnd])

  /**
   * Pause speech - uses native pause()
   */
  const pauseSpeech = useCallback(() => {
    if (!isSupported || !isPlaying || isPaused) return

    try {
      window.speechSynthesis.pause()

      setIsPaused(true)
      setIsPlaying(false)

      onPause?.()
    } catch (error) {
      console.error("Pause error:", error)
    }
  }, [isSupported, isPlaying, isPaused, onPause])

  /**
   * Resume speech - uses native resume()
   */
  const resumeSpeech = useCallback(() => {
    if (!isSupported || !isPaused) return

    try {
      window.speechSynthesis.resume()

      setIsPaused(false)
      setIsPlaying(true)

      onResume?.()
    } catch (error) {
      console.error("Resume error:", error)
    }
  }, [isSupported, isPaused, onResume])

  /**
   * Stop speech completely
   */
  const stopSpeech = useCallback(() => {
    if (!isSupported) return

    try {
      window.speechSynthesis.cancel()
      resetState()
      onStop?.()
    } catch (error) {
      console.error("Stop error:", error)
      resetState()
    }
  }, [isSupported, resetState, onStop])

  /**
   * Handle main button click
   */
  const handleButtonClick = useCallback(() => {
    if (disabled || !isSupported || !text) return

    if (isPlaying) {
      pauseSpeech()
    } else if (isPaused) {
      resumeSpeech()
    } else {
      startSpeech()
    }
  }, [disabled, isSupported, text, isPlaying, isPaused, startSpeech, pauseSpeech, resumeSpeech])

  /**
   * Handle stop button click
   */
  const handleStopClick = useCallback((e) => {
    e.stopPropagation()
    stopSpeech()
  }, [stopSpeech])

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      handleButtonClick()
    } else if (e.key === "Escape" && (isPlaying || isPaused)) {
      e.preventDefault()
      stopSpeech()
    }
  }, [handleButtonClick, isPlaying, isPaused, stopSpeech])

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel()
      }
    }
  }, [])

  /**
   * Stop speech when text changes
   */
  useEffect(() => {
    if (isPlaying || isPaused) {
      stopSpeech()
    }
  }, [text])

  // Don't render if not supported or no text
  if (!isSupported || !text) {
    return null
  }

  // Determine icon
  const getIcon = () => {
    if (isPlaying) return <FaPause />
    if (isPaused) return <FaPlay />
    return <FaVolumeUp />
  }

  // Determine aria label
  const getAriaLabel = () => {
    if (isPlaying) return "Pause speech"
    if (isPaused) return "Resume speech"
    return "Start reading text"
  }

  // Show stop button when active
  const isActive = isPlaying || isPaused

  return (
    <Container>
      <ButtonWrapper>
        <MainButton
          onClick={handleButtonClick}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-label={getAriaLabel()}
          aria-pressed={isPlaying}
          $isPlaying={isPlaying}
          type="button"
        >
          <IconWrapper>
            <IconContainer $visible={!isPlaying && !isPaused}>
              <FaVolumeUp />
            </IconContainer>
            <IconContainer $visible={isPlaying}>
              <FaPause />
            </IconContainer>
            <IconContainer $visible={isPaused}>
              <FaPlay />
            </IconContainer>
          </IconWrapper>
        </MainButton>
        <StopButton
          onClick={handleStopClick}
          aria-label="Stop speech"
          type="button"
          $isActive={isActive}
        >
          <FaTimes />
        </StopButton>
      </ButtonWrapper>
    </Container>
  )
}

export default TextToSpeech

// Styled Components

const pulseAnimation = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.9; }
  100% { transform: scale(1); opacity: 1; }
`

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-left: 8px;
`

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const MainButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  border: 2px solid var(--lumiq-secondary, #929292);
  background-color: ${(props) => (props.disabled ? "#f5f5f5" : "#ffffff")};
  color: ${(props) => (props.disabled ? "#cccccc" : "var(--lumiq-secondary, #505050)")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  font-size: 18px;
  padding: 0;
  outline: none;
  position: relative;
  z-index: 2;

  animation: ${(props) => (props.$isPlaying ? pulseAnimation : "none")} 2s ease-in-out infinite;

  &:hover:not(:disabled) {
    border-color: var(--lumiq-tertiary, #0066cc);
    background-color: #f0f8ff;
    transform: scale(1.05);
  }

  &:focus:not(:disabled) {
    outline: 2px solid var(--lumiq-tertiary, #0066cc);
    outline-offset: 2px;
    border-color: var(--lumiq-tertiary, #0066cc);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
    font-size: 20px;
  }
`

const IconWrapper = styled.div`
  position: relative;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 767px) {
    width: 20px;
    height: 20px;
  }
`

const IconContainer = styled.span`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transform: ${(props) => (props.$visible ? "scale(1) rotate(0deg)" : "scale(0.5) rotate(-90deg)")};
  transition: opacity 0.25s ease, transform 0.25s ease;
`

const StopButton = styled.button`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) ${(props) => (props.$isActive ? "translateX(44px)" : "translateX(0)")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border-radius: 50%;
  border: 1px solid var(--lumiq-secondary, #ff4444);
  background-color: #ffffff;
  color: var(--lumiq-secondary, #ff4444);
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  outline: none;
  z-index: 1;
  opacity: ${(props) => (props.$isActive ? 1 : 0)};
  pointer-events: ${(props) => (props.$isActive ? "auto" : "none")};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
              opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background-color: var(--lumiq-secondary, #ff4444);
    color: #ffffff;
    transform: translate(-50%, -50%) translateX(44px) scale(1.1);
  }

  &:focus {
    outline: 2px solid var(--lumiq-secondary, #ff4444);
    outline-offset: 2px;
  }

  &:active {
    transform: translate(-50%, -50%) translateX(44px) scale(0.95);
  }

  @media (max-width: 767px) {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
    font-size: 16px;
    transform: translate(-50%, -50%) ${(props) => (props.$isActive ? "translateX(50px)" : "translateX(0)")};

    &:hover {
      transform: translate(-50%, -50%) translateX(50px) scale(1.1);
    }

    &:active {
      transform: translate(-50%, -50%) translateX(50px) scale(0.95);
    }
  }
`
